/**
 * The Admin's side of the central (W9-CENTRAL-NEW-ORDER-CHAT, D473, ADR-0091; DevMobility H-001, H-003, H-007, I-081, and the
 * findings A-40 and D-34) — framework-neutral view-models:
 *
 *  - **Operações › Conversas das corridas** (`ride_chats`): the market's live conversations (the ones waiting for the central
 *    first), one ride's whole thread — what the rider and the driver said to each other and to the central — and the composer:
 *    who to write to, the text, or one of the governed ready messages of the market in the Admin's language.
 *  - the ride's chat HISTORY on its detail page (H-007): shown only when the ride has messages.
 *  - the courier incidents queue (D-34): what a courier reported (store closed, recipient absent, cancel) and how an operator
 *    closed it.
 *  - the operator's intervention on a Send that is stuck (A-40): the edges a status change may take and the proof an evidence
 *    step needs.
 *
 * The console only presents: the server authorizes every call at the market asked for and decides every outcome. Neutral: no
 * country, currency or language is named here.
 */
import type { CourierIncidentCode, CourierIncidentOpsView, CourierIncidentResolutionView, DeliveryStatus, OpsChatMessage, OpsChatRecipient, OpsChatThreadSummary, ProofInput } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

// ---- authority (display only — the server re-authorizes every call) ------------------------------------------------

export function rideChatAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly maySend: boolean } {
  if (grants === undefined) return { mayView: false, maySend: false };
  return { mayView: grantsAllow(grants, { permission: 'ride_chat:read', target: 'GEO' }), maySend: grantsAllow(grants, { permission: 'ride_chat:send', target: 'GEO' }) };
}

export function courierIncidentAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayResolve: boolean } {
  if (grants === undefined) return { mayView: false, mayResolve: false };
  return { mayView: grantsAllow(grants, { permission: 'courier_job:view', target: 'GEO' }), mayResolve: grantsAllow(grants, { permission: 'courier_job:release', target: 'GEO' }) };
}

// ---- the conversations -----------------------------------------------------------------------------------------------------

/** The conversations list as the central works it: waiting-for-the-central first, then the most recently active. */
export function sortThreads(threads: readonly OpsChatThreadSummary[]): readonly OpsChatThreadSummary[] {
  return [...threads].sort((a, b) => (a.awaitingOperator !== b.awaitingOperator ? (a.awaitingOperator ? -1 : 1) : a.lastMessageAt < b.lastMessageAt ? 1 : a.lastMessageAt > b.lastMessageAt ? -1 : 0));
}

/** The recipients an operator may address on a ride: the driver only once one is assigned. */
export function chatRecipients(hasDriver: boolean): readonly OpsChatRecipient[] {
  return hasDriver ? ['rider', 'driver', 'both'] : ['rider'];
}

/** Who spoke: the role the bubble is labelled by. */
export const chatSpeaker = (m: Pick<OpsChatMessage, 'senderRole'>): 'rider' | 'driver' | 'operator' => m.senderRole;

/** Whether a message is a participant's reply to the central (it is what an operator is asked to answer). */
export const isReplyToCentral = (m: Pick<OpsChatMessage, 'senderRole' | 'recipient'>): boolean => m.senderRole !== 'operator' && m.recipient === 'operator';

// ---- the courier incidents ---------------------------------------------------------------------------------------------------

export const COURIER_INCIDENT_RESOLUTIONS: readonly CourierIncidentResolutionView[] = ['resumed', 'courier_released', 'order_cancelled', 'dismissed'];

/** Open incidents first (oldest waiting first — they are the queue), then the resolved ones, newest first. */
export function sortIncidents(list: readonly CourierIncidentOpsView[]): readonly CourierIncidentOpsView[] {
  return [...list].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'OPEN' ? -1 : 1;
    return a.status === 'OPEN' ? (a.createdAt < b.createdAt ? -1 : 1) : a.createdAt < b.createdAt ? 1 : -1;
  });
}

export const courierIncidentCodes = (): readonly CourierIncidentCode[] => ['store_closed', 'recipient_absent', 'courier_cancel'];

// ---- the intervention on a stuck Send (A-40) ---------------------------------------------------------------------------------

/** The plain lifecycle edges of a Send (`@vello/delivery`), spelled as data; evidence steps (delivered / returned) and the policy-gated `confirmed` are separate actions. */
const SEND_EDGES: Readonly<Record<DeliveryStatus, readonly DeliveryStatus[]>> = {
  draft: ['cancelled'],
  confirmed: ['courier_assigned', 'cancelled'],
  courier_assigned: ['picked_up', 'cancelled', 'failed'],
  picked_up: ['in_transit', 'return_initiated', 'failed'],
  in_transit: ['return_initiated', 'failed'],
  return_initiated: ['failed'],
  delivered: [],
  returned: [],
  cancelled: [],
  failed: ['return_initiated'],
};

export interface SendInterventions {
  /** The plain status changes an operator may make now (never the evidence steps). */
  readonly statuses: readonly DeliveryStatus[];
  /** `confirm` — a draft passes the policy gates and is confirmed. */
  readonly mayConfirm: boolean;
  /** `deliver` — the handover with proof (an in-transit Send). */
  readonly mayDeliver: boolean;
  /** `return` — the return with proof (a return in progress). */
  readonly mayReturn: boolean;
  /** Custody events may be appended while the Send is live (not settled). */
  readonly mayRecordCustody: boolean;
}

export function sendInterventions(status: DeliveryStatus): SendInterventions {
  const settled = status === 'delivered' || status === 'returned' || status === 'cancelled';
  return {
    statuses: SEND_EDGES[status],
    mayConfirm: status === 'draft',
    mayDeliver: status === 'in_transit',
    mayReturn: status === 'return_initiated',
    mayRecordCustody: !settled,
  };
}

/** The proof an operator submits: the method, the reference (or the handover code for a PIN) and when it was captured. */
export function sendProof(input: { readonly method: ProofInput['method']; readonly reference: string; readonly at: string }): ProofInput | undefined {
  const ref = input.reference.trim();
  if (ref === '') return undefined;
  return input.method === 'pin' ? { method: 'pin', providedPin: ref, capturedAt: input.at } : { method: input.method, evidenceRef: ref, capturedAt: input.at };
}

